/**
 * Web Push po stronie przeglądarki (PLAN.md §8).
 *
 * Subskrypcja żyje w service workerze; backend trzyma jej endpoint i klucze,
 * żeby móc obudzić telefon, gdy appka jest zamknięta.
 */

import { api } from "../api";

export type PushState = "unsupported" | "denied" | "off" | "on";

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalised);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

function keyOf(subscription: PushSubscription, name: "p256dh" | "auth"): string {
  const key = subscription.getKey(name);
  if (!key) throw new Error(`Subskrypcja push bez klucza ${name}`);
  return window.btoa(String.fromCharCode(...new Uint8Array(key)));
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  return existing ?? (await navigator.serviceWorker.ready);
}

export async function currentState(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.getRegistration();
  const subscription = await reg?.pushManager.getSubscription();
  return subscription ? "on" : "off";
}

/** Poproś o zgodę, zasubskrybuj i zgłoś subskrypcję backendowi. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";

  const config = await api.pushConfig();
  if (!config.enabled || !config.public_key) {
    throw new Error("Backend nie ma kluczy VAPID - uruchom `python -m app.push_keys`.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "off";

  const reg = await registration();
  const subscription =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.public_key) as BufferSource,
    }));

  await api.pushSubscribe({
    endpoint: subscription.endpoint,
    keys: { p256dh: keyOf(subscription, "p256dh"), auth: keyOf(subscription, "auth") },
  });
  return "on";
}

export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration();
  const subscription = await reg?.pushManager.getSubscription();
  if (subscription) {
    await api.pushUnsubscribe(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  }
  return "off";
}
